/**
 * Pure XP / level rules. The SERVER is the source of truth for awarded XP (the
 * client never writes its own XP — see docs/BARRAGE_IMPLEMENTATION_PLAN.md §4);
 * this module only provides the deterministic level<->xp maths shared by the
 * Cloud Function (to award) and the UI (to draw the bar / ceremony).
 *
 * PROGRESSIVE curve (owner request): early levels come quickly, but each level
 * costs MORE than the last, so reaching the final rank (level {@link MAX_LEVEL} =
 * POPSTAR) is *very* hard but not impossible. The cumulative XP to reach level `L`
 * is {@link XP_BASE}·(L−1)^{@link XP_LEVEL_EXP}; the per-level cost therefore keeps
 * climbing. The level is resolved from the cumulative total in one shot (with a
 * tiny rounding correction), so a single large grant that crosses several levels is
 * always placed correctly.
 *
 * Calibration note: with the current reward rate (~100 XP per second survived),
 * level 2 lands in the first run (~18s), but the cumulative XP to hit level 256 is
 * tens of millions — hundreds of hours of cumulative survival. Tune via
 * {@link XP_BASE} (early cost) and {@link XP_LEVEL_EXP} (how brutal the top gets).
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { AttackFamily } from './types';

/** New accounts begin at this level (legacy started clients at 1; §2.3/plan). */
export const STARTING_LEVEL = 1;

/**
 * Highest attainable level (the POPSTAR capstone). Mirrors
 * {@link import('./ranks').MAX_RANK_LEVEL} (kept as a local constant so this module
 * stays dependency-free); XP beyond this level no longer advances the rank.
 */
export const MAX_LEVEL = 256;

/**
 * Cumulative XP to reach level 2 (i.e. the cost of the very first level). The whole
 * curve scales off this, so it sets how quick the early game feels.
 */
export const XP_BASE = 1800;

/**
 * Curve exponent: cumulative XP to reach level `L` is `XP_BASE·(L−1)^XP_LEVEL_EXP`.
 * >1 makes every level cost progressively more than the last; higher = a steeper,
 * grindier top end. 1.82 puts level 256 at ~43M XP.
 */
export const XP_LEVEL_EXP = 1.82;

/**
 * A decomposition of a total XP amount into level + progress, for the XP bar and
 * the level-up ceremony. `intoLevel`/`toNext` are relative to the CURRENT level's
 * (variable) span, so the bar fraction is `intoLevel / (intoLevel + toNext)`.
 */
export interface LevelInfo {
  /** Current level (new accounts start at 1), clamped to {@link MAX_LEVEL}. */
  level: number;
  /** XP earned within the current level (0 .. this level's span). */
  intoLevel: number;
  /** XP remaining to reach the next level (0 once at {@link MAX_LEVEL}). */
  toNext: number;
}

/**
 * The XP earned by surviving a run, as a function of the *attacks dodged* (legacy
 * `XPTOGIVE = ATK_COUNT * mult`, §2.8). It is intentionally NOT a function of the
 * raw score — survival time already drives the leaderboard; XP rewards engaging
 * with the dangerous patterns. (The live server policy derives XP from the
 * validated score instead — see `functions/src/util.runRewards`; this table is kept
 * for parity / future use.)
 */
export const XP_PER_DODGE = 1;

/**
 * Per-family XP multiplier applied to each dodged hazard of that family
 * (legacy §2.8: ×2 for the crusher types 1–3 = walls + turret, ×3 for the caro).
 * Families absent from the table earn the ×1 base rate. Typed against
 * {@link AttackFamily} so a key typo (e.g. the old `wall` vs the canonical `walls`)
 * is a compile error rather than a silently-skipped bonus.
 */
export const XP_FAMILY_MULTIPLIER: Partial<Record<AttackFamily, number>> = {
  walls: 2,
  turret: 2,
  caro: 3,
} as const;

/**
 * Total XP required to *reach* the start of a given level — the cumulative curve.
 * Level {@link STARTING_LEVEL} starts at 0; the cost climbs as `(L−1)^exp`. Levels
 * above {@link MAX_LEVEL} clamp to the capstone's threshold.
 *
 * @param level a level ≥ {@link STARTING_LEVEL}.
 * @returns the cumulative XP at which that level begins.
 */
export function xpForLevel(level: number): number {
  const lvl = Number.isFinite(level) && level > STARTING_LEVEL ? Math.floor(level) : STARTING_LEVEL;
  const capped = lvl > MAX_LEVEL ? MAX_LEVEL : lvl;
  if (capped <= STARTING_LEVEL) return 0;
  return Math.round(XP_BASE * Math.pow(capped - STARTING_LEVEL, XP_LEVEL_EXP));
}

/**
 * Resolve a cumulative XP total into the current level and intra-level progress.
 *
 * Uses the closed-form inverse of {@link xpForLevel} as an estimate, then nudges by
 * at most a level to absorb `Math.round` boundary error — so it's exact and still
 * O(1). Correct for an arbitrarily large `totalXp` (a multi-level grant lands on the
 * right level directly) and clamped to {@link MAX_LEVEL}.
 *
 * @param totalXp lifetime XP for the account (server-authoritative). Negative or
 *   non-finite inputs are floored to 0 defensively.
 * @returns the level + progress decomposition.
 */
export function levelForXp(totalXp: number): LevelInfo {
  const xp = Number.isFinite(totalXp) && totalXp > 0 ? Math.floor(totalXp) : 0;

  // Closed-form estimate: invert xp = XP_BASE·(level−1)^exp.
  let level = STARTING_LEVEL + Math.floor(Math.pow(xp / XP_BASE, 1 / XP_LEVEL_EXP));
  if (!Number.isFinite(level) || level < STARTING_LEVEL) level = STARTING_LEVEL;
  if (level > MAX_LEVEL) level = MAX_LEVEL;
  // Correct for rounding so that xpForLevel(level) <= xp < xpForLevel(level+1).
  while (level < MAX_LEVEL && xpForLevel(level + 1) <= xp) level++;
  while (level > STARTING_LEVEL && xpForLevel(level) > xp) level--;

  const base = xpForLevel(level);
  const intoLevel = xp - base;
  const toNext = level >= MAX_LEVEL ? 0 : xpForLevel(level + 1) - xp;
  return { level, intoLevel, toNext };
}

/**
 * Whether granting `gainedXp` on top of `beforeXp` crosses at least one level
 * boundary (so the server/UI can trigger the level-up ceremony). Reports the
 * number of levels gained, which may be >1 for a big grant.
 *
 * @param beforeXp lifetime XP before the grant.
 * @param gainedXp XP being added (non-negative).
 * @returns `{ leveledUp, levelsGained, before, after }` level decompositions.
 */
export function applyXpGain(
  beforeXp: number,
  gainedXp: number,
): { leveledUp: boolean; levelsGained: number; before: LevelInfo; after: LevelInfo } {
  const safeBefore = Number.isFinite(beforeXp) && beforeXp > 0 ? Math.floor(beforeXp) : 0;
  const gain = Number.isFinite(gainedXp) && gainedXp > 0 ? Math.floor(gainedXp) : 0;
  const before = levelForXp(safeBefore);
  const after = levelForXp(safeBefore + gain);
  const levelsGained = after.level - before.level;
  return { leveledUp: levelsGained > 0, levelsGained, before, after };
}
